/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.ui.codemirror

import io.peekandpoke.klang.codemirror.ext.Completion
import io.peekandpoke.klang.codemirror.ext.CompletionContext
import io.peekandpoke.klang.codemirror.ext.CompletionResult
import io.peekandpoke.klang.script.intel.CompletionProvider
import io.peekandpoke.klang.script.intel.CompletionSuggestion
import io.peekandpoke.klang.script.types.KlangType
import io.peekandpoke.kraft.utils.jsObject

/**
 * Creates a CodeMirror completion source that suggests DSL symbols from the [EditorDocContext].
 *
 * Returns a function compatible with CodeMirror's `CompletionConfig.override`.
 */
fun dslCompletionSource(docContext: EditorDocContext): (CompletionContext) -> CompletionResult? {
    return { context ->
        completeDsl(context, docContext)
    }
}

private fun completeDsl(context: CompletionContext, docContext: EditorDocContext): CompletionResult? {
    // Match word being typed (letters, digits, underscores)
    val word = context.matchBefore(js("/\\w*/"))
    if (word == null || (word.from == word.to && !context.explicit)) {
        return null
    }

    val from: Int = word.from.unsafeCast<Int>()
    val to: Int = word.to.unsafeCast<Int>()
    val typed = context.state.doc.sliceString(from, to)

    // Skip if inside a string literal
    if (isInsideString(context, from)) {
        return null
    }

    // Force immediate re-parse so the analysis matches the current editor text.
    // The debounced onCodeChanged may not have fired yet when completion triggers.
    val currentCode = context.state.doc.toString()
    docContext.processCodeImmediate(currentCode)

    val provider = CompletionProvider(docContext.registry)
    val options = mutableListOf<Completion>()

    // Detect member access context: is there a '.' immediately before the word?
    val charBefore = if (from > 0) {
        context.state.doc.sliceString(from - 1, from)
    } else {
        ""
    }
    val isMemberAccess = charBefore == "."

    if (isMemberAccess) {
        val receiverType = inferReceiverTypeBeforeDot(docContext, from - 1)
        if (receiverType != null) {
            val suggestions = provider.memberCompletions(receiverType, typed)
            suggestions.mapTo(options) { it.toCodeMirrorCompletion() }
        }
        // If receiver type can't be inferred (stale AST, parse error), show nothing.
        // Showing top-level symbols after a dot is always wrong.
    } else {
        // Where an argument starts in a known call, its unfilled parameters as `name = ` first, in declaration order:
        // they are what this call takes. CodeMirror ranks by match quality first, so the boost lifts them above a local
        // or a library name that matches as well, and the declaration order holds among them.
        val params = docContext.lastAnalysis?.namedArgumentsAt(pos = from, text = currentCode) ?: emptyList()
        val paramSuggestions = provider.namedArgumentCompletions(params, typed)

        paramSuggestions.forEachIndexed { index, suggestion ->
            options.add(suggestion.toCodeMirrorCompletion().also { it.boost = LOCAL_BOOST + paramSuggestions.size - index })
        }

        // The song's own names next: the user typed them, they are the most likely value. The boost lifts a local
        // above a library name that matches as well.
        val locals = docContext.lastAnalysis?.localsAt(pos = from, text = currentCode) ?: emptyList()
        provider.localCompletions(locals, typed).mapTo(options) { suggestion ->
            suggestion.toCodeMirrorCompletion().also { it.boost = LOCAL_BOOST }
        }

        val suggestions = provider.topLevelCompletions(typed)
        suggestions.mapTo(options) { it.toCodeMirrorCompletion() }
    }

    // Add import completion if typing an import statement
    if (isImportContext(context)) {
        val importSuggestions = provider.importCompletions(docContext.availableLibraryNames)
        importSuggestions.mapTo(options) { it.toCodeMirrorCompletion() }
    }

    if (options.isEmpty()) {
        return null
    }

    return jsObject<CompletionResult> {
        this.from = from
        this.options = options.toTypedArray()
    }
}

/**
 * CodeMirror rank boost for the song's own names over library names that match the typed word as well. A named
 * argument's boost sits above it, counting down in declaration order (CodeMirror adds the boost to the match score,
 * whose kinds of match are 100 apart, so it never lifts a worse kind of match).
 */
private const val LOCAL_BOOST = 1

/** Resolve the receiver type for a dot-completion via the analyzer's single entry point. */
private fun inferReceiverTypeBeforeDot(docContext: EditorDocContext, dotPos: Int): KlangType? {
    return docContext.lastAnalysis?.receiverTypeBeforeDot(dotPos - 1)
}

/** Convert a [CompletionSuggestion] to a CodeMirror [Completion] JS object. */
private fun CompletionSuggestion.toCodeMirrorCompletion(): Completion = jsObject {
    label = this@toCodeMirrorCompletion.label
    // A named argument inserts more than its label shows (`q = ` for `q =`)
    val insert = insertText

    if (insert != label) {
        apply = insert
    }

    type = when (kind) {
        CompletionSuggestion.Kind.FUNCTION -> "function"
        CompletionSuggestion.Kind.PROPERTY -> "variable"
        CompletionSuggestion.Kind.KEYWORD -> "keyword"
        CompletionSuggestion.Kind.PARAMETER -> "property"
    }
    val d = this@toCodeMirrorCompletion.detail
    if (d.isNotEmpty()) {
        this.detail = d
    }
    if (description.isNotEmpty()) {
        info = description
    }
}

/**
 * Check if the cursor is inside a string literal by scanning for unescaped quotes.
 * Handles both double-quote ("...") and single-quote ('...') strings.
 */
private fun isInsideString(context: CompletionContext, wordFrom: Int): Boolean {
    val line = context.state.doc.lineAt(wordFrom)
    val prefix = context.state.doc.sliceString(line.from, wordFrom)
    return isInsideStringLiteral(prefix)
}

/**
 * Returns true if the end of [prefix] falls inside an unclosed string literal.
 *
 * Scans for unescaped `"` and `'` — when we encounter an opening quote, everything until
 * the matching closing quote (or end of prefix) is inside a string. Escaped quotes (`\"`, `\'`)
 * are skipped. Handles both double-quote and single-quote strings as used in KlangScript.
 *
 * Shared between [dslCompletionSource] and [dslEditorExtension].
 */
internal fun isInsideStringLiteral(prefix: String): Boolean {
    var inString = false
    var openQuote = "" // the quote string that opened the current string
    var i = 0
    while (i < prefix.length) {
        val ch = prefix[i].toString()
        if (inString) {
            if (ch == "\\") {
                i += 2
                continue // skip escaped character
            }
            if (ch == openQuote) {
                inString = false // closing quote
            }
        } else {
            if (ch == "\"" || ch == "'") {
                inString = true
                openQuote = ch
            }
        }
        i++
    }
    return inString
}

/**
 * Check if the cursor is in an import statement context (after `from "`).
 *
 * The line must start with `import`: `from` is an ordinary name outside an import (`range(from = 200, to = 400)`),
 * so a line that merely contains it is no import.
 */
private fun isImportContext(context: CompletionContext): Boolean {
    val line = context.state.doc.lineAt(context.pos)
    val lineText = context.state.doc.sliceString(line.from, context.pos)
    return importLineStart.containsMatchIn(lineText) && lineText.contains("from") && lineText.contains("\"")
}

/** The word `import` at the start of a line: `importer = 1` and `important(...)` are no import. */
private val importLineStart = Regex("""^\s*import\b""")
